import React, {
  Suspense,
  forwardRef,
  lazy,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Button, message, Spin } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { useQuote } from "../../QuoteProvider";
import { ErrorBoundary } from "../../../ErrorBoundary";
import { trackQuoteBookNowClicked } from "../../../../utils/quoteAnalytics";
import BookingPaymentLoading from "./BookingPaymentLoading";
import {
  usePreparedBookingPayment,
  buildBookingInputsKey,
} from "../../hooks/usePreparedBookingPayment";
import "./book-now-button.css";

// Lazy-loaded so Stripe.js (@stripe/stripe-js) and the Payment Element code
// stay OUT of the eager funnel main.js bundle (respects the funnel-bundle
// guardrail) and only load when a patient actually clicks Book Now.
const BookingPaymentPanel = lazy(() => import("./BookingPaymentPanel"));

// If the lazy panel chunk fails to load (stale deploy / CDN 404 on the split
// bundle — a known funnel-bundle-split risk in this repo), don't strand the
// patient on a spinner: fall back to the hosted Stripe invoice page, which we
// already hold in panel state (outside the failed chunk).
const HostedRedirect: React.FC<{ url: string }> = ({ url }) => {
  useEffect(() => {
    window.location.href = url;
  }, [url]);
  return (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <Spin />
    </div>
  );
};

// When the panel was launched from a PREPARED PaymentIntent (no hosted URL),
// a chunk-load failure can't redirect to Stripe. Show an error toast and
// restore the Book Now button so the patient's next click takes the legacy
// fetch path (the prepared payload is discarded via onChunkFail).
const PreparedChunkFail: React.FC<{ onChunkFail: () => void }> = ({
  onChunkFail,
}) => {
  useEffect(() => {
    message.error(
      "Payment form failed to load — please tap Book Now again.",
    );
    onChunkFail();
    // Mount-only by design (fires the toast + reset exactly once). No
    // eslint-disable directive: the react-hooks plugin isn't installed and
    // the prod webpack ESLint pass hard-fails on directives naming unknown
    // rules (see the #395/#399 deploy incident).
  }, []);
  return null;
};

interface BookNowButtonProps {
  quoteId: string;
  disabled?: boolean;
  priceCents?: number;
  // Fired when the inline payment panel opens/closes, so the parent
  // (QuoteSummaryTreatment) can collapse the procedures/price summary into a
  // deposit-focused view while the card form is up. `depositCents` is the
  // authoritative server-charged amount, so the parent's hero matches "Pay $X".
  onPaymentModeChange?: (active: boolean, depositCents?: number) => void;
}

// In-page payment state: when the server returns a confirmable client_secret,
// we mount the Stripe Payment Element inline instead of redirecting.
// url is null for prepared-PI payments (no hosted Stripe invoice page exists).
interface PaymentPanelState {
  clientSecret: string;
  publishableKey: string;
  amountCents: number;
  url: string | null;
}

// Ghost-tap guard: if the user just tapped "Next" on the previous step and
// this button mounted under their finger, the buffered click sends them to
// Stripe without intent. Block clicks for 500ms after mount — same pattern as
// the promo-code button in QuoteSummaryTreatment.
const GHOST_TAP_GUARD_MS = 500;

// Imperative handle so a sibling (the "Free evaluation" confirmation modal in
// QuoteSummaryTreatment) can route its "Book now" into this ONE button's
// existing flow — reusing markBookNowPressed + pre-warm + the payment panel,
// with no second instance (no double-stamp, no double pre-warm).
export interface BookNowButtonHandle {
  trigger: () => void;
}

const BookNowButton = forwardRef<BookNowButtonHandle, BookNowButtonProps>((
  { quoteId, disabled, priceCents, onPaymentModeChange },
  ref,
) => {
  const quoteData = useQuote();
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [panel, setPanel] = useState<PaymentPanelState | null>(null);
  // Admin test mode: a staff member viewing the public funnel while signed
  // into the admin dashboard carries the same-origin access_token cookie.
  // GET /login echoes the signed-in user (or null). We offer the $1 "Book
  // Now" ONLY to users holding the ADMIN role — a COORDINATOR (or any other
  // signed-in role) must see the real booking price. Price is server-enforced
  // (create-booking-invoice's auth.isAdminRequest re-checks the ADMIN role),
  // so this is display/intent only.
  const [isAdmin, setIsAdmin] = useState(false);
  // Whether the async /login admin check has completed (admin or not). The fast
  // path is gated on this so an admin can't tap through a stale $500 prepared PI
  // before isAdmin flips true and the hook re-prepares at $1.
  const [adminChecked, setAdminChecked] = useState(false);

  // Pre-warm: prepare a bare PaymentIntent before the patient clicks so the
  // Payment Element is ready instantly. Only meaningful when quoteId is
  // available and target is BOOKING_PRICE (otherwise no promo applies here).
  // The hook is 404-safe (unavailable = kill switch off → falls back
  // automatically to the legacy create-booking-invoice path on click).
  const preparedPromoCode =
    quoteData.appliedPromoCode?.target === "BOOKING_PRICE"
      ? quoteData.appliedPromoCode.code
      : undefined;

  // The booking tier selector ($1 admin smoke test / $99 held F&F pass / $500
  // standard) — the SINGLE source of truth for BOTH the prepared PI and the
  // legacy click path. They MUST request the same tier or the two paths charge
  // different amounts: the 2026-06 gift-$99 bug was exactly this, where the
  // prepared path omitted the tier and the server defaulted every gift/admin
  // booking to $500. isAdmin resolves async (see the /login effect below); when
  // it flips true, bookingPriceCents changes 50000→100.
  const bookingPriceCents = isAdmin ? 100 : quoteData.giftPass ? 9900 : 50000;

  // Gate the FIRST prepare on adminChecked. The admin $1 tier is only known once
  // /login resolves — preparing before that fires a $500 prepare at mount, then
  // a SECOND $1 prepare when isAdmin flips. If the first hasn't persisted its PI
  // id yet, both have previousPiId=null and create SEPARATE racing PIs instead
  // of updating one in place. The claim helper then serves the slot WINNER (the
  // $500) to the loser ($1) request — an admin who pressed "$1" gets a $500 PI.
  // That is the 2026-06-15 "$1 admin test charged $500" incident. By waiting for
  // adminChecked the funnel issues exactly ONE prepare, already at the final
  // tier, so there is nothing to race. /login resolves well within the 500ms
  // ghost-tap guard, so pre-warm is still ready by the time a click is allowed;
  // if it ever lags, the click falls back to the (correct) server-validated
  // click path. The server still re-checks the admin cookie + a valid pass —
  // bookingPriceCents is only ever a hint it authoritatively validates.
  const prepareQuoteId = adminChecked ? quoteId : null;

  const {
    payload: preparedPayload,
    status: preparedStatus,
    inputsKey: preparedInputsKey,
    discard: discardPrepared,
  } = usePreparedBookingPayment({
    quoteId: prepareQuoteId,
    promoCode: preparedPromoCode,
    giftPassCode: quoteData.giftPass?.code,
    bookingPriceCents,
  });

  useEffect(() => {
    const t = setTimeout(() => setReady(true), GHOST_TAP_GUARD_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL || ""}/login`,
          { credentials: "include" },
        );
        if (res.ok) {
          const user = await res.json();
          const roles: string[] = Array.isArray(user?.roles)
            ? user.roles
            : [];
          if (!cancelled && roles.includes("ADMIN")) setIsAdmin(true);
        }
      } catch {
        /* not signed in / not admin — leave isAdmin false */
      } finally {
        // Admin determination complete (admin or not). Gating the fast path on
        // this closes the race where an admin taps through a stale $500 prepared
        // PI before /login resolves. /login resolves well within the 500ms
        // ghost-tap guard, so this never delays a real click; if it ever hangs,
        // clicks fall back to the (correct, server-validated) click path.
        if (!cancelled) setAdminChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClick = async (opts?: { fromTrigger?: boolean }) => {
    // The `ready` (ghost-tap) guard blocks buffered clicks right after mount. A
    // call from the eval-modal's "Book now" (opts.fromTrigger) is a deliberate
    // late action, NOT a ghost tap — bypass the guard so a fast resume-mode user
    // (photos already present, BookNowButton freshly mounted) can't dead-click.
    if (!quoteId) return;
    // The rendered Button carries `disabled` (today: photos not uploaded yet),
    // but trigger() calls straight into this function and bypassed the DOM's
    // disabled state entirely — so the eval modal's "Book now" could open a
    // real payment panel on a quote the button itself refuses to book. Guard
    // here, inside the shared path, so BOTH entry points obey one rule.
    if (disabled) return;
    if (!ready && !opts?.fromTrigger) return;

    // Intent-only click event — fired for every tap, including ones that
    // later abort/fail, before any booking mutation runs. NEVER a conversion
    // signal on its own.
    trackQuoteBookNowClicked({
      fromTrigger: !!opts?.fromTrigger,
      isAdmin,
      prepared: preparedStatus === "ready" && !!preparedPayload,
      bookingPriceCents,
    });

    // Best-effort breadcrumb so a coordinator can see the patient started the
    // booking flow (NOT booked — that's confirmed on payment). Skip admin $1
    // test sessions so they don't pollute the signal. Never blocks booking.
    if (!isAdmin) {
      try {
        quoteData.markBookNowPressed();
      } catch {
        /* never let the breadcrumb break booking */
      }
    }

    // Fast path: if the prepared PaymentIntent is ready AND the inputs
    // (promo/gift-pass) haven't changed since it was prepared, open the panel
    // instantly — no server round-trip at click time. The inputs key comparison
    // guards against the (rare) race where a promo was applied after the last
    // prepare fired but before the re-prepare response arrived.
    const currentInputsKey = buildBookingInputsKey(
      quoteId,
      preparedPromoCode,
      quoteData.giftPass?.code,
      bookingPriceCents,
    );
    // Overcharge guard: NEVER open a prepared PI that would charge MORE than the
    // tier we're advertising. The server now reports the SERVED PI's real amount
    // (prepare-booking-payment.ts), so a concurrent-prepare race that handed this
    // request a higher-tier winner (the $500-charged-as-$1 incident) shows up
    // here as amountCents > bookingPriceCents → we skip the fast path and take
    // the synchronous, server-authoritative click path instead (which forces the
    // correct tier server-side). A BOOKING_PRICE promo only REDUCES the amount
    // (<= tier), so legitimately-discounted prepares still take the fast path.
    const preparedAmountSafe =
      !!preparedPayload && preparedPayload.amountCents <= bookingPriceCents;
    if (
      adminChecked &&
      preparedStatus === "ready" &&
      preparedPayload &&
      preparedAmountSafe &&
      preparedInputsKey === currentInputsKey
    ) {
      setPanel({
        clientSecret: preparedPayload.clientSecret,
        publishableKey: preparedPayload.publishableKey,
        amountCents: preparedPayload.amountCents,
        url: null, // bare PI — no hosted Stripe invoice page
      });
      onPaymentModeChange?.(true, preparedPayload.amountCents);
      return;
    }

    // Legacy (fallback) path: create-booking-invoice on click. Used when the
    // kill switch is off, the hook errored, or the promo changed mid-flight.
    setLoading(true);
    try {
      // Send the BASE tier ($99 followUp / $500 standard) — NOT a
      // pre-discounted amount. The server applies any BOOKING_PRICE promo
      // exactly once (createBookingFeeInvoice → applyBookingPromoDiscount).
      // Pre-subtracting the promo here would double-apply it (server applies
      // again) or land on the $99 tier and trip the eligibility gate. The
      // server validates this against the allowed tier set. ($249 is the
      // signed email-link tier and never originates from this page.)
      //
      // Admin test mode → $1 (100¢). The server re-checks the admin cookie
      // and only honors $1 for a real admin session, so this is intent only.
      //
      // Gift pass: $99 authorized by the pass (pass wins over promo/discount).
      // The legacy followUpDiscount (discountStatus auto-$99) lane was retired
      // 2026-06-11 — the funnel only renders $99 for a Friends & Family Pass the
      // patient holds; team-issued $99 arrives via signed /action/book links.
      // bookingPriceCents is computed once at component scope (above) so this
      // click path and the prepared-PI path always request the identical tier.

      // Self-book funnel now uses the new Stripe Invoice flow
      // (/api/create-booking-invoice), which creates a real Invoice +
      // Booking + Payment in the payments model and supports booking promo
      // codes server-side (createBookingFeeInvoice → applyBookingPromoDiscount).
      // This replaces the legacy /api/create-booking-checkout Checkout
      // Session path (cutover 2026-05-30). The legacy route remains mounted
      // server-side as a fallback but is no longer called from the funnel.
      const endpoint = "/api/create-booking-invoice";
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || ""}${endpoint}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            quoteId,
            bookingPriceCents,
            promoCode:
              quoteData.appliedPromoCode?.target === "BOOKING_PRICE"
                ? quoteData.appliedPromoCode.code
                : undefined,
            // Friends & Family Pass code — server validates + stamps attribution.
            giftPassCode: quoteData.giftPass?.code,
          }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        // A 400 while a pass is attached means the pass didn't authorize this
        // booking (invalid/expired/flag off — wording varies by lane): clear
        // it so the funnel reverts to standard pricing for the retry. Other
        // statuses (5xx/Stripe hiccups) keep the pass — they're transient.
        if (response.status === 400 && quoteData.giftPass) {
          quoteData.clearGiftPass();
        }
        throw new Error(error.error || "Failed to create checkout session");
      }

      const { url, clientSecret, publishableKey, amountCents } =
        await response.json();

      // Preferred path: pay IN-PAGE via the Stripe Payment Element (no
      // redirect — the patient stays on the funnel). The server returns
      // clientSecret + publishableKey when the booking invoice's native
      // PaymentIntent is confirmable. When it can't (already paid, fetch
      // failed) those are absent and we fall back to the hosted invoice page —
      // today's behavior, so a paying patient is never stranded.
      if (clientSecret && publishableKey && url) {
        const depositCents =
          typeof amountCents === "number" ? amountCents : bookingPriceCents;
        setPanel({
          clientSecret,
          publishableKey,
          amountCents: depositCents,
          url,
        });
        setLoading(false);
        // Tell the parent to collapse the procedures/price summary into the
        // deposit-focused view, passing the exact amount we're charging.
        onPaymentModeChange?.(true, depositCents);
        return;
      }

      if (!url) throw new Error("No checkout URL received");

      window.location.href = url;
    } catch (err: any) {
      console.error("Booking error:", err);
      message.error(err?.message || "Failed to start checkout");
      setLoading(false);
    }
  };

  const handlePanelCancel = () => {
    setPanel(null);
    // Restore the full procedures/price summary.
    onPaymentModeChange?.(false);
  };

  const handleChunkFail = () => {
    // Discard the prepared payload so the next click takes the legacy path.
    discardPrepared();
    setPanel(null);
    onPaymentModeChange?.(false);
  };

  // Expose trigger() so the "Free evaluation" confirmation modal (in
  // QuoteSummaryTreatment) can route its "Book now" into this exact flow. No
  // deps array → the handle always calls the freshest handleClick (which closes
  // over ready/adminChecked/prepared state), so the fast path + ghost-tap guard
  // behave identically to a real tap. (react-hooks plugin isn't installed here.)
  useImperativeHandle(ref, () => ({
    trigger: () => {
      void handleClick({ fromTrigger: true });
    },
  }));

  // Once the in-page Payment Element is live, it replaces the Book Now button.
  if (panel) {
    // When the panel was opened from a prepared PI (url === null), there is no
    // hosted Stripe page to redirect to on a chunk-load failure — instead show
    // an error toast and restore the button (PreparedChunkFail handles that).
    const errorFallback =
      panel.url != null ? (
        <HostedRedirect url={panel.url} />
      ) : (
        <PreparedChunkFail onChunkFail={handleChunkFail} />
      );

    return (
      <ErrorBoundary fallback={errorFallback}>
        <Suspense fallback={<BookingPaymentLoading />}>
          <BookingPaymentPanel
            quoteId={quoteId}
            clientSecret={panel.clientSecret}
            publishableKey={panel.publishableKey}
            amountCents={panel.amountCents}
            fallbackUrl={panel.url ?? undefined}
            onCancel={handlePanelCancel}
          />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Button label: when the prepared payload is ready, prefer its amountCents
  // (server-authoritative, includes admin $1 + promo). Otherwise fall back to
  // the priceCents prop from the parent (pre-promo estimate).
  const displayPriceCents =
    preparedStatus === "ready" && preparedPayload
      ? preparedPayload.amountCents
      : priceCents;

  // Admin label must reflect the REAL amount, never a hardcoded "$1". The label
  // is what the admin reads before paying — if a stray race ever served a $500
  // PI, advertising "$1" here is exactly how the overcharge stayed invisible.
  // Once the prepared payload is ready we show its server-reported amount; until
  // then we show the intended $1 (the tier we requested).
  const adminLabelCents =
    preparedStatus === "ready" && preparedPayload
      ? preparedPayload.amountCents
      : 100;

  return (
    <Button
      type="primary"
      size="large"
      icon={<CalendarOutlined />}
      onClick={() => handleClick()}
      loading={loading}
      disabled={disabled || !quoteId || !ready}
      className="book-now-button"
    >
      {isAdmin
        ? `Book Now $${Math.round(adminLabelCents / 100)} (admin test)`
        : displayPriceCents != null
          ? `Book Now $${Math.round(displayPriceCents / 100)}`
          : "Book Now"}
    </Button>
  );
});

BookNowButton.displayName = "BookNowButton";

export default BookNowButton;
